import Logger from '@framework/sentry/Logger';
import { addQueryParams, getQueryString } from '@framework/router/nextRouter';
import type { QuerySanitizer } from './validators/types';

interface IRedirectToDefaultFiltersParams {
  query: Record<string, string | string[]>,
  defaults: Record<string, string | string[]>,
  validators?: Record<string, QuerySanitizer>,
}

const getOverrideValue = (
  key: string,
  defaultValue: string | string[],
  query: Record<string, string | string[]>,
  validators?: Record<string, QuerySanitizer>,
): string | string[] | undefined => {
  if (query[key] === undefined) return defaultValue;

  const validator = validators?.[key];
  if (!validator) return undefined;

  const sanitized = validator(query[key]);
  if (!sanitized) return defaultValue;
  if (sanitized !== query[key]) return sanitized;

  return undefined;
};

const collectOverrides = (
  defaults: Record<string, string | string[]>,
  query: Record<string, string | string[]>,
  validators?: Record<string, QuerySanitizer>,
): Record<string, string | string[]> => {
  const overrides: Record<string, string | string[]> = {};

  for (const [key, defaultValue] of Object.entries(defaults)) {
    const override = getOverrideValue(key, defaultValue, query, validators);
    if (override !== undefined) {
      overrides[key] = override;
    }
  }

  return overrides;
};

const getDefaultQueryFilters = ({
  query,
  defaults,
  validators,
}: IRedirectToDefaultFiltersParams): string | null => {
  const overrides = collectOverrides(defaults, query, validators);

  if (!Object.keys(overrides).length) return null;

  const mergedQuery = addQueryParams(query, overrides);
  const mergedQueryString = getQueryString(mergedQuery);

  if (mergedQueryString === getQueryString(query)) {
    Logger.error({
      message: 'Default query filters resolve to the current URL, skipping redirect',
      context: { query, defaults, overrides },
    });

    return null;
  }

  return `?${ mergedQueryString }`;
};

export default getDefaultQueryFilters;
